import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button, Input, Alert, Card, CardBody } from '../components/ui';
import { ArbieMascot } from '../components/ArbieMascot';
import { extractApiErrorMessage } from '../api/api-error';

const BASE_URL = import.meta.env.VITE_API_URL || '';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Missing reset token. Please use the link from your password reset email.');
    } else {
      // Strip the token from the URL bar so it doesn't persist in browser history
      window.history.replaceState(null, '', '/reset-password');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(extractApiErrorMessage(data, 'Failed to reset password'));
      }

      // No session is issued by design — a reset ends sessions rather than
      // starting one. Send the user to sign in with their new password.
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-12">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <ArbieMascot className="mx-auto h-16 w-16" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-gray-100">
            Choose a new password
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            You'll be signed out everywhere else.
          </p>
        </div>

        <Card>
          <CardBody>
            {error && (
              <Alert variant="error" className="mb-4">
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  disabled={!token || isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="confirm"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Confirm password
                </label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat your password"
                  disabled={!token || isLoading}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={!token || isLoading}
                loading={isLoading}
              >
                Update password
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-500"
              >
                Request a new link
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
